/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Input, Button, Image, Popconfirm } from 'antd-v5';
import { notificationError, notificationSuccess } from '../../../../../../core/actions';
import {
  updateClientActivity,
  addActivityAttachments,
  deleteActivityAttachment,
} from '../../../../../../services/clients';
import { generatePresignedUrl, uploadToS3 } from '../../../../../../services/attachments';

const MAX_FILES_SIZE = 1e9;
const MAX_FILES_SIZE_HUMAN = '1GB';
const MAX_NEW_FILES_PER_UPLOAD = 5;

const FILE_ICONS = {
  pdf: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-pdf.png',
  csv: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-csv.png',
  xls: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-xls.png',
  xlsx: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-xls.png',
  doc: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-doc.png',
  docx: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-doc.png',
  txt: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-txt.png',
  default: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/file-rect-file.svg',
};

const ACCEPTED_FILE_TYPES = '.jpg, .jpeg, .png, .gif, .svg, .pdf, .csv, .doc, .docx, .txt, .xls, .xlsx';

const getFileExtension = fileName => {
  return fileName?.split('.').pop()?.toLowerCase() || '';
};

const getFileIcon = (fileType, fileName) => {
  const ext = getFileExtension(fileName);
  if (FILE_ICONS[ext]) return FILE_ICONS[ext];
  if (fileType?.startsWith('image/')) return null; // Will show actual image preview
  return FILE_ICONS.default;
};

const isImageFile = fileType => {
  return fileType?.startsWith('image/');
};

const EditActivityModal = props => {
  const {
    id,
    clientId,
    notes: initialNotes,
    attachments: existingAttachments = [],
    supportsAttachments = false,
    toggleEditModal,
    onSuccess,
  } = props;
  const [notes, setNotes] = useState(initialNotes || '');
  const [newAttachments, setNewAttachments] = useState([]);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const flags = useFlags();

  // Filter out deleted attachments from display
  const displayedExistingAttachments = (existingAttachments || []).filter(
    att => !deletedAttachmentIds.includes(att.id),
  );

  const canAddMore = newAttachments.length < MAX_NEW_FILES_PER_UPLOAD;
  const hasEditingAttachment = newAttachments.some(att => att.edit);

  const handleDownloadAttachment = attachment => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.fileName || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteExistingAttachment = attachmentId => {
    setDeletedAttachmentIds([...deletedAttachmentIds, attachmentId]);
  };

  const addNewAttachment = () => {
    const attachment = { id: Math.floor(Math.random() * 1000000), file: null, url: null, description: '', edit: true };
    setCurrentAttachment(attachment);
    setNewAttachments([...newAttachments, attachment]);
  };

  const handleSaveAttachment = () => {
    const savedAttachment = { ...currentAttachment, edit: false };
    const filteredAttachments = newAttachments.map(att => (att.id === savedAttachment.id ? savedAttachment : att));
    setNewAttachments(filteredAttachments);
    setCurrentAttachment(null);
    setIsEditing(false);
  };

  const handleAttachFile = e => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size >= MAX_FILES_SIZE) {
      notificationError(`File size exceeds ${MAX_FILES_SIZE_HUMAN}`);
      return;
    }
    setCurrentAttachment({
      ...currentAttachment,
      file,
      url: URL.createObjectURL(file),
      fileName: file.name,
      fileType: file.type,
    });
    e.target.value = null;
  };

  const handleCancelAttachment = attachmentId => {
    setCurrentAttachment(null);
    if (!isEditing) {
      setNewAttachments(newAttachments.filter(a => a.id !== attachmentId));
    } else {
      setNewAttachments(newAttachments.map(a => (a.id === attachmentId ? { ...a, edit: false } : a)));
    }
    setIsEditing(false);
  };

  const handleRemoveAttachment = attachmentId => {
    setNewAttachments(newAttachments.filter(a => a.id !== attachmentId));
  };

  const uploadAttachmentsToS3 = async attachmentsToUpload => {
    const uploadPromises = attachmentsToUpload.map(async attachment => {
      const { fileUrl, presignedUrl } = await generatePresignedUrl({
        fileType: attachment.file.type,
        attachmentType: 'activity',
      });
      await uploadToS3({ presignedUrl, file: attachment.file });
      return {
        fileName: attachment.file.name, // Use original filename
        fileType: attachment.file.type,
        url: fileUrl,
        description: attachment.description || null,
      };
    });
    return Promise.all(uploadPromises);
  };

  const saveActivityHandler = async () => {
    setIsSaving(true);
    try {
      // 1. Update notes
      await updateClientActivity({ clientId, activityId: id, notes });

      // 2. Delete removed attachments
      if (deletedAttachmentIds.length > 0) {
        await Promise.all(
          deletedAttachmentIds.map(attachmentId =>
            deleteActivityAttachment({ clientId, activityId: id, attachmentId }),
          ),
        );
      }

      // 3. Upload new files to S3 and add attachments
      let attachmentsData = [];
      if (newAttachments.length > 0) {
        attachmentsData = await uploadAttachmentsToS3(newAttachments);
        await addActivityAttachments({ clientId, activityId: id, attachments: attachmentsData });
      }

      notificationSuccess('Activity Saved!');
      // Combine remaining existing attachments with new ones
      const remainingAttachments = (existingAttachments || []).filter(att => !deletedAttachmentIds.includes(att.id));
      onSuccess({ id, notes, attachments: [...remainingAttachments, ...attachmentsData] });
      toggleEditModal(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      notificationError('Failed To Save Activity');
    } finally {
      setIsSaving(false);
    }
  };

  const renderFilePreview = (url, fileType, fileName, size = 50) => {
    const icon = getFileIcon(fileType, fileName);
    if (isImageFile(fileType) && url) {
      return (
        <Image
          src={url}
          alt={fileName || 'attachment'}
          style={{ width: size, height: size, objectFit: 'cover', borderRadius: '4px' }}
          fallback={FILE_ICONS.default}
          preview={false}
        />
      );
    }
    return (
      <img
        src={icon || FILE_ICONS.default}
        alt={fileName || 'file'}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  };

  return (
    <Modal
      title="Edit Activity"
      open
      onCancel={() => toggleEditModal(false)}
      onOk={saveActivityHandler}
      okText="Save"
      okButtonProps={{ loading: isSaving, disabled: hasEditingAttachment }}
      cancelButtonProps={{ disabled: isSaving }}
      destroyOnClose
      width={500}
      centered
    >
      <Input.TextArea rows={7} placeholder="Write a note" value={notes} onChange={e => setNotes(e.target.value)} />

      {/* Existing Attachments Section */}
      {displayedExistingAttachments.length > 0 && (
        <div className="m-top-15">
          <p className="fs-13 fw-500 m-btm-8" style={{ color: '#666' }}>
            Attached Files
          </p>
          <div className="flex-col-left gap-8">
            {displayedExistingAttachments.map(attachment => (
              <div
                key={attachment.id || attachment.url}
                className="flex-row-center gap-8 rem-pad-width"
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  width: '100%',
                }}
              >
                {renderFilePreview(attachment.url, attachment.fileType, attachment.fileName, 40)}
                <div className="flex-col-left" style={{ flex: 1, minWidth: 0 }}>
                  <span
                    className="fs-13 fw-500"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {attachment.fileName || 'File'}
                  </span>
                  {attachment.description && (
                    <span className="fs-12" style={{ color: '#888' }}>
                      {attachment.description}
                    </span>
                  )}
                </div>
                <Button type="text" size="small" onClick={() => handleDownloadAttachment(attachment)} title="Download">
                  <FontAwesomeIcon icon={['far', 'arrow-down-to-line']} style={{ color: '#33AAFC' }} />
                </Button>
                <Popconfirm
                  title="Delete attachment"
                  description="Are you sure you want to delete this attachment?"
                  onConfirm={() => handleDeleteExistingAttachment(attachment.id)}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" size="small" title="Delete">
                    <FontAwesomeIcon icon={['far', 'trash']} style={{ color: '#ff4d4f' }} />
                  </Button>
                </Popconfirm>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Attachments Section */}
      {flags.files && supportsAttachments && (
        <div className="m-top-15">
          {newAttachments.length > 0 && (
            <div className="flex-col-left gap-8 m-btm-15">
              {newAttachments.map(attachment =>
                !attachment.edit ? (
                  <div
                    key={attachment.id}
                    className="flex-row-center gap-8"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#e6f4ff',
                      borderRadius: '8px',
                      width: '100%',
                    }}
                  >
                    {renderFilePreview(attachment.url, attachment.fileType, attachment.fileName, 40)}
                    <div className="flex-col-left" style={{ flex: 1, minWidth: 0 }}>
                      <span
                        className="fs-13 fw-500"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {attachment.fileName || 'File'}
                      </span>
                      {attachment.description && (
                        <span className="fs-12" style={{ color: '#888' }}>
                          {attachment.description}
                        </span>
                      )}
                    </div>
                    <Popconfirm
                      title="Remove attachment"
                      description="Are you sure you want to remove this attachment?"
                      onConfirm={() => handleRemoveAttachment(attachment.id)}
                      okText="Remove"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      disabled={hasEditingAttachment}
                    >
                      <Button type="text" size="small" disabled={hasEditingAttachment} title="Remove">
                        <FontAwesomeIcon icon={['far', 'trash']} style={{ color: '#ff4d4f' }} />
                      </Button>
                    </Popconfirm>
                  </div>
                ) : (
                  <div
                    key={attachment.id}
                    className="flex-row-center gap-8 rem-pad-width"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fafafa',
                      borderRadius: '8px',
                      border: '1px dashed #d9d9d9',
                      width: '100%',
                    }}
                  >
                    {currentAttachment?.url ? (
                      <div
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onClick={() =>
                          setCurrentAttachment({
                            ...currentAttachment,
                            file: null,
                            url: null,
                            fileName: null,
                            fileType: null,
                          })
                        }
                      >
                        {renderFilePreview(
                          currentAttachment.url,
                          currentAttachment.fileType,
                          currentAttachment.fileName,
                        )}
                        <div
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            backgroundColor: '#ff4d4f',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'times']} style={{ color: '#fff', fontSize: 10 }} />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex-col-center"
                        style={{
                          cursor: 'pointer',
                          color: '#33AAFC',
                          height: 50,
                          width: 50,
                          backgroundColor: '#E6F4FF',
                          borderRadius: '8px',
                        }}
                        onClick={() => document.getElementById('edit-activity-file-input').click()}
                      >
                        <FontAwesomeIcon icon="far fa-paperclip" size="lg" />
                      </div>
                    )}
                    <Input
                      placeholder="Describe the file"
                      value={currentAttachment?.description || ''}
                      onChange={e => setCurrentAttachment({ ...currentAttachment, description: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      size="small"
                      disabled={!currentAttachment?.file}
                      onClick={handleSaveAttachment}
                    >
                      Save
                    </Button>
                    <Button type="default" size="small" onClick={() => handleCancelAttachment(attachment.id)}>
                      Cancel
                    </Button>
                  </div>
                ),
              )}
            </div>
          )}

          <Button
            type="dashed"
            disabled={!canAddMore || hasEditingAttachment}
            onClick={addNewAttachment}
            icon={<FontAwesomeIcon icon="far fa-plus" />}
          >
            Add file
          </Button>
          <input
            id="edit-activity-file-input"
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleAttachFile}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </Modal>
  );
};

export default EditActivityModal;
